import { useEffect, useState } from 'react';
import { Code, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';
import { getWidgets, updateWidget } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';

const AdminWidgets = () => {
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  useEffect(() => {
    fetchWidgets();
  }, []);

  const fetchWidgets = async () => {
    try {
      const res = await getWidgets();
      setWidgets(res.data);
    } catch (error) {
      toast.error('Failed to load widgets');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (widget) => {
    setSaving(prev => ({ ...prev, [widget.id]: true }));

    try {
      await updateWidget(widget.id, {
        code: widget.code,
        is_active: widget.is_active
      });
      toast.success(`${widget.name} updated successfully`);
    } catch (error) {
      toast.error(`Failed to update ${widget.name}`);
    } finally {
      setSaving(prev => ({ ...prev, [widget.id]: false }));
    }
  };

  const handleChange = (id, field, value) => {
    setWidgets(prev => prev.map(w => 
      w.id === id ? { ...w, [field]: value } : w
    ));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-widgets-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
            Widgets
          </h1>
          <p className="text-neutral-500 mt-2">
            Paste your widget embed codes here. These will appear on your website.
          </p>
        </div>

        <div className="space-y-6">
          {widgets.map((widget) => (
            <div 
              key={widget.id}
              className="admin-card"
              data-testid={`widget-${widget.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-neutral-100 rounded-lg flex items-center justify-center">
                    <Code className="w-5 h-5 text-neutral-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{widget.name}</h3>
                    <p className="text-sm text-neutral-500">
                      {widget.id === 'contact' && 'Contact form or booking widget'}
                      {widget.id === 'availability' && 'Calendar or availability checker'}
                      {widget.id === 'client-login' && 'Client portal login widget'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    {widget.code ? (
                      <span className="flex items-center gap-1 text-sm text-green-600">
                        <Check className="w-4 h-4" />
                        Configured
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-sm text-neutral-400">
                        <X className="w-4 h-4" />
                        Not configured
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={widget.is_active}
                      onCheckedChange={(checked) => handleChange(widget.id, 'is_active', checked)}
                      data-testid={`widget-active-${widget.id}`}
                    />
                    <Label className="text-sm">Active</Label>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor={`code-${widget.id}`}>Embed Code</Label>
                <Textarea
                  id={`code-${widget.id}`}
                  value={widget.code || ''}
                  onChange={(e) => handleChange(widget.id, 'code', e.target.value)}
                  placeholder={`Paste your ${widget.name.toLowerCase()} embed code here...`}
                  rows={6}
                  className="font-mono text-sm"
                  data-testid={`widget-code-${widget.id}`}
                />
                <p className="text-xs text-neutral-500">
                  Accepts HTML/JavaScript embed codes from your booking or CRM system
                </p>
              </div>

              <div className="flex justify-end mt-4">
                <Button 
                  onClick={() => handleSave(widget)}
                  className="bg-neutral-900 hover:bg-neutral-800"
                  disabled={saving[widget.id]}
                  data-testid={`save-widget-${widget.id}`}
                >
                  {saving[widget.id] ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Saving...
                    </span>
                  ) : (
                    'Save Widget'
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Preview Info */}
        <div className="admin-card mt-8 bg-neutral-50">
          <h3 className="font-semibold mb-2">How it works</h3>
          <ul className="space-y-2 text-sm text-neutral-600">
            <li>1. Copy the embed code from your booking/CRM system</li>
            <li>2. Paste it in the corresponding widget field above</li>
            <li>3. Toggle "Active" to show the widget on your website</li>
            <li>4. Click "Save Widget" to apply changes</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminWidgets;
